<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { ReportFilters } from '~/models/ReportFilters'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck, faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import useGroupsStore from '~/stores/groups'
import { useAuthStore } from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import Button from '~/components/ui/Button.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'

const props = defineProps<{
  filters?: ReportFilters | undefined
  isInitializing?: boolean
}>()

const emit = defineEmits<{
  'update:filters': [value: ReportFilters | undefined]
}>()

const { t } = useI18n()

function updateFilters(patch: Partial<ReportFilters>) {
  const next: ReportFilters = {
    ...(props.filters ?? {}),
    ...patch,
  }
  emit('update:filters', next)
}

const groupsStore = useGroupsStore()
const { sortedGroups } = storeToRefs(groupsStore)

const authStore = useAuthStore()
const { userSubjectsByGrades } = storeToRefs(authStore)

const periodOptions = ['today', 'last7', 'last30', 'spring', 'autumn', 'schoolYear'] as const
const openSections = ref({
  group: false,
  subject: false,
  period: false,
})

const updateSectionStates = () => {
  if (!props.filters) {
    return
  }

  openSections.value.group = !props.filters.selectedGroup
  openSections.value.subject = !props.filters.selectedSubjects || props.filters.selectedSubjects.length === 0
  openSections.value.period = false
}

const toggleSection = (section: 'group' | 'subject' | 'period') => {
  openSections.value[section] = !openSections.value[section]
}

function toggleSubject(subject: SubjectCode) {
  const currentSubjects = props.filters?.selectedSubjects || []
  const isSelected = currentSubjects.includes(subject)

  let newSubjects: SubjectCode[]
  if (isSelected) {
    newSubjects = currentSubjects.filter(s => s !== subject)
  } else {
    newSubjects = [...currentSubjects, subject]
  }

  updateFilters({ selectedSubjects: newSubjects })
}

function toggleAllSubjects(value: boolean | 'indeterminate') {
  const allSubjects = userSubjectsByGrades.value || []

  if (value === true) {
    updateFilters({ selectedSubjects: [...allSubjects] })
  } else {
    updateFilters({ selectedSubjects: [] })
  }
}

const areAllSubjectsSelected = computed(() => {
  const currentSubjects = props.filters?.selectedSubjects || []
  const allSubjects = userSubjectsByGrades.value || []
  return allSubjects.length > 0 && currentSubjects.length === allSubjects.length
})

const shouldShowGroupSelection = computed(() => {
  return !openSections.value.group && props.filters?.selectedGroup
})

const shouldShowSubjectSelection = computed(() => {
  return !openSections.value.subject && props.filters?.selectedSubjects && props.filters.selectedSubjects.length > 0
})

const isGroupSelected = computed(() => {
  return (groupId: string) => props.filters?.selectedGroup?.groupId === groupId
})

const selectedSubjectsDisplayText = computed(() => {
  if (areAllSubjectsSelected.value) {
    return t('reports.sidebar.all')
  }
  return props.filters?.selectedSubjects?.map(subject => t(`metadata.subjects.${subject}`)).join(', ') || ''
})

const isSubjectSelected = computed(() => {
  return (subject: SubjectCode) => props.filters?.selectedSubjects?.includes(subject) || false
})

const isPeriodSelected = computed(() => {
  return (period: string) => props.filters?.selectedPeriod === period
})

const isPeriodSelectedWithDefault = computed(() => {
  return (period: string) => (props.filters?.selectedPeriod || 'today') === period
})

watch(() => [props.filters, props.isInitializing], () => {
  if (props.filters) {
    updateSectionStates()
  }
}, { deep: true })

onMounted(() => {
  if (props.filters) {
    updateSectionStates()
  }
})
</script>

<template>
  <aside
    :aria-label="t('reports.sidebar.ariaLabel')"
    class="w-full shrink-0"
    tabindex="-1"
  >
    <div tabindex="-1">
      <div class="rounded-2xl" tabindex="-1">
        <div class="overflow-auto">
          <div v-if="!props.isInitializing" class="flex flex-col gap-6 p-4 max-lg:px-0 lg:p-4">
            <div class="space-y-2">
              <div class="border-green-40 rounded border bg-white">
                <button
                  class="group flex w-full cursor-pointer items-center justify-between rounded py-3 focus-visible:ring"
                  :data-pendo="PendoFeature.ActivityReports.GroupFilter"
                  @click="toggleSection('group')"
                >
                  <div class="mx-4 flex-1 text-left">
                    <span class="text-green-40 font-bold underline-offset-4 group-hover:underline">
                      {{ t('reports.sidebar.group') }}
                    </span>
                    <div v-if="shouldShowGroupSelection" class="mt-1 flex items-center gap-2">
                      <div class="bg-green-30 h-2 w-2 rounded-full" />
                      <span class="text-green-40 truncate text-sm">{{ props.filters?.selectedGroup?.name }}</span>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    :icon="openSections.group ? faMinus : faPlus"
                    class="text-green-40 mr-4"
                  />
                </button>
                <Transition
                  :duration="500"
                  name="nested"
                >
                  <div
                    v-show="openSections.group"
                    class="outer border-t-gray-10 mx-4 border-t py-3"
                  >
                    <div class="inner flex flex-col gap-1">
                      <Button
                        v-for="group in sortedGroups"
                        :key="group.groupId"
                        size="md"
                        variant="ghost"
                        shade="dark"
                        color="green"
                        class="w-full justify-start"
                        :class="{ '!bg-green-10 !text-green-60 !border-green-40 !border-2 !font-semibold': isGroupSelected(group.groupId) }"
                        @click="updateFilters({ selectedGroup: group })"
                      >
                        <div class="flex w-full justify-between">
                          <span class="truncate first-letter:capitalize">{{ group.name }}</span>
                          <FontAwesomeIcon
                            v-if="isGroupSelected(group.groupId)"
                            :icon="faCheck"
                            class="text-green-60 h-4 w-4 shrink-0"
                          />
                        </div>
                      </Button>
                    </div>
                  </div>
                </Transition>
              </div>

              <div class="border-green-40 rounded border bg-white">
                <button
                  class="group flex w-full cursor-pointer items-center justify-between rounded py-3 focus-visible:ring"
                  :data-pendo="PendoFeature.ActivityReports.SubjectFilter"
                  @click="toggleSection('subject')"
                >
                  <div class="mx-4 flex-1 text-left">
                    <span class="text-green-40 font-bold underline-offset-4 group-hover:underline">
                      {{ t('reports.sidebar.subject') }}
                    </span>
                    <div v-if="shouldShowSubjectSelection" class="mt-1 flex items-start gap-2">
                      <div class="bg-green-30 mt-1 h-2 w-2 shrink-0 rounded-full" />
                      <span class="text-green-40 text-sm break-words">
                        {{ selectedSubjectsDisplayText }}
                      </span>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    :icon="openSections.subject ? faMinus : faPlus"
                    class="text-green-40 mr-4"
                  />
                </button>
                <Transition
                  :duration="500"
                  name="nested"
                >
                  <div
                    v-show="openSections.subject"
                    class="outer border-t-gray-10 mx-4 border-t py-3"
                  >
                    <div class="inner flex flex-col gap-2">
                      <label class="hover:bg-gray-5 flex cursor-pointer items-center gap-3 rounded p-2">
                        <AuCheckbox
                          :model-value="areAllSubjectsSelected"
                          @update:model-value="toggleAllSubjects"
                        />
                        <span class="text-green-40 text-sm">{{ t('reports.sidebar.allSubjects') }}</span>
                      </label>
                      <label
                        v-for="subject in userSubjectsByGrades"
                        :key="subject"
                        class="hover:bg-gray-5 flex cursor-pointer items-center gap-3 rounded p-2"
                      >
                        <AuCheckbox
                          :model-value="isSubjectSelected(subject)"
                          @update:model-value="toggleSubject(subject)"
                        />
                        <span class="text-green-40 text-sm first-letter:capitalize">{{ t(`metadata.subjects.${subject}`) }}</span>
                      </label>
                    </div>
                  </div>
                </Transition>
              </div>

              <div class="border-green-40 rounded border bg-white">
                <button
                  class="group flex w-full cursor-pointer items-center justify-between rounded py-3 focus-visible:ring"
                  :data-pendo="PendoFeature.ActivityReports.PeriodFilter"
                  @click="toggleSection('period')"
                >
                  <div class="mx-4 flex-1 text-left">
                    <span class="text-green-40 font-bold underline-offset-4 group-hover:underline">
                      {{ t('reports.sidebar.period') }}
                    </span>
                    <div v-if="!openSections.period" class="mt-1 flex items-center gap-2">
                      <div class="bg-green-30 h-2 w-2 rounded-full" />
                      <span class="text-green-40 truncate text-sm">{{ t(`reports.periods.${props.filters?.selectedPeriod}`) }}</span>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    :icon="openSections.period ? faMinus : faPlus"
                    class="text-green-40 mr-4"
                  />
                </button>
                <Transition
                  :duration="500"
                  name="nested"
                >
                  <div
                    v-show="openSections.period"
                    class="outer border-t-gray-10 mx-4 border-t py-3"
                  >
                    <div class="inner flex flex-col gap-1">
                      <Button
                        v-for="period in periodOptions"
                        :key="period"
                        size="md"
                        variant="ghost"
                        shade="dark"
                        color="green"
                        class="w-full justify-start"
                        :class="{ '!bg-green-10 !text-green-60 !border-green-40 !border-2 !font-semibold': isPeriodSelected(period) }"
                        @click="updateFilters({ selectedPeriod: period })"
                      >
                        <div class="flex w-full justify-between">
                          <span class="truncate first-letter:capitalize">{{ t(`reports.periods.${period}`) }}</span>
                          <FontAwesomeIcon
                            v-if="isPeriodSelectedWithDefault(period)"
                            :icon="faCheck"
                            class="text-green-60 h-4 w-4 shrink-0"
                          />
                        </div>
                      </Button>
                    </div>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s linear;
  max-height: 1000px;
}

.nested-leave-active {
  transition-delay: 0.2s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-5px);
  opacity: 0;
  max-height: 0;
}

.nested-enter-active .inner,
.nested-leave-active .inner {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  will-change: transform, opacity;
}

.nested-enter-active .inner {
  transition-delay: 0.2s;
}

.nested-enter-from .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}

.nested-leave-to .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
</style>
